/**
 * platform-tools-registry.ts
 *
 * Builds Platform-level tools in the GeminiTool format (declaration + handler),
 * compatible with the existing GeminiAvatar tools.ts infrastructure.
 *
 * Adapts VinGroupHub's hub-tools-registry for the Starglobal3D platform context:
 *   1. Platform navigation tools (open project in iframe, close project)
 *   2. Sub-project proxy tools (navigate scene, open model, IoT, etc.)
 *      These forward commands to the sub-project iframe via postMessage.
 *
 * Key difference from VinHub: projects are loaded dynamically from API,
 * not hardcoded. The agent uses open_project_in_viewer to open any project URL.
 */

import type { GeminiTool } from '@/agents/gemini-live/lib/tools';
import type { CommandResult } from '../hooks/usePlatformMessageBridge';
import type { PlatformProject } from '../components/ProjectViewerModal/platform-project-viewer-modal';
import projectService from '../utils/services/project';
import functionCallingServices from '@/utils/services/functionCalling';

/* ─── Types ─── */
export interface PlatformToolContext {
    /** Open a project inside the iframe modal */
    openProject: (project: PlatformProject) => void;
    /** Close the currently viewed project modal */
    closeProject: () => void;
    /** Get the currently active project (null if none open) */
    getActiveProject: () => PlatformProject | null;
    /** Send a command to the active sub-project iframe and await its response */
    sendIframeCommand: (action: string, params: Record<string, any>) => Promise<CommandResult>;
}

/* ─── Shared helper: check a project is open before sending iframe commands ─── */
function requireActiveProject(ctx: PlatformToolContext): { error: true; result: object } | { error: false } {
    const active = ctx.getActiveProject();
    if (!active) {
        return {
            error: true,
            result: {
                success: false,
                error: 'Chưa có dự án nào được mở trong trình xem. Hãy mở một dự án trước bằng cách gọi open_project_in_viewer.',
                instruction: 'Thông báo cho người dùng rằng cần mở dự án trước, rồi gợi ý gọi open_project_in_viewer.',
            },
        };
    }
    return { error: false };
}

/* ─── Standard proxy tool builder to reduce repetition ─── */
function proxyTool(
    name: string,
    description: string,
    parameters: any,
    ctx: PlatformToolContext,
): GeminiTool {
    return {
        declaration: { name, description, parameters },
        handler: async (args: any) => {
            const check = requireActiveProject(ctx);
            if (check.error) return check.result;
            try {
                const result = await ctx.sendIframeCommand(name, args);
                return result.success ? result.data : { success: false, error: result.error };
            } catch (err: any) {
                return { success: false, error: err.message };
            }
        },
    };
}

/* ─── Build GeminiTool registry ─── */
export function buildPlatformToolsRegistry(ctx: PlatformToolContext): Record<string, GeminiTool> {
    return {
        /* ═══════════════════════════════════════════════════════════
         * PLATFORM NAVIGATION TOOLS
         * ═══════════════════════════════════════════════════════════ */

        open_project_in_viewer: {
            declaration: {
                name: 'open_project_in_viewer',
                description:
                    'Mở một dự án 3D/360 trong trình xem nhúng (iframe viewer) để người dùng có thể tương tác trực tiếp. Gọi khi người dùng yêu cầu mở/xem/khám phá một dự án cụ thể. Ví dụ: "mở dự án Ajinomoto", "cho tôi xem nhà máy Đạm Cà Mau", "mở bảo tàng Phụ nữ".',
                parameters: {
                    type: 'OBJECT' as const,
                    properties: {
                        project_url: {
                            type: 'STRING',
                            description: 'URL của dự án cần mở (ví dụ: /smart-factory-360/ajinomoto/). Có thể là đường dẫn tương đối hoặc URL đầy đủ.',
                        },
                        project_name: {
                            type: 'STRING',
                            description: 'Tên hiển thị của dự án (ví dụ: "Nhà máy Ajinomoto")',
                        },
                    },
                    required: ['project_url', 'project_name'],
                },
            },
            handler: async (args: { project_url: string; project_name: string }) => {
                const project: PlatformProject = {
                    id: `agent-${Date.now()}`,
                    url: args.project_url,
                    title: args.project_name,
                    display_vn: args.project_name,
                    display_en: args.project_name,
                };
                ctx.openProject(project);
                return {
                    success: true,
                    project: { url: args.project_url, name: args.project_name },
                    instruction: `Đã mở dự án "${args.project_name}" trong trình xem. Hãy giới thiệu ngắn gọn dự án và cho người dùng biết họ có thể khám phá các scene, mô hình 3D, IoT, và các tính năng khác bên trong.`,
                };
            },
        },

        close_project_viewer: {
            declaration: {
                name: 'close_project_viewer',
                description: 'Đóng dự án đang xem trong trình xem nhúng và quay về trang Platform.',
                parameters: {
                    type: 'OBJECT' as const,
                    properties: {},
                },
            },
            handler: async () => {
                ctx.closeProject();
                return {
                    success: true,
                    instruction: 'Đã đóng trình xem dự án và quay về trang Platform. Hỏi người dùng muốn khám phá dự án nào tiếp.',
                };
            },
        },

        get_current_viewer_project: {
            declaration: {
                name: 'get_current_viewer_project',
                description: 'Kiểm tra dự án nào đang được mở trong trình xem nhúng.',
                parameters: {
                    type: 'OBJECT' as const,
                    properties: {},
                },
            },
            handler: async () => {
                const active = ctx.getActiveProject();
                if (!active) {
                    return {
                        success: true,
                        currentProject: null,
                        message: 'Chưa có dự án nào được mở trong trình xem.',
                        instruction: 'Thông báo cho người dùng chưa mở dự án nào và gợi ý mở một dự án.',
                    };
                }
                return {
                    success: true,
                    currentProject: {
                        id: active.id,
                        name: active.display_vn || active.title || active.display_en,
                        url: active.url,
                    },
                };
            },
        },

        /**
         * Fetch real project list from database API.
         * Agent MUST call this before attempting to open any project.
         */
        list_platform_projects: {
            declaration: {
                name: 'list_platform_projects',
                description:
                    'Lấy danh sách dự án thực tế từ database. BẮT BUỘC gọi trước khi mở dự án. Có thể lọc theo lĩnh vực (field) hoặc tìm kiếm bằng từ khóa (search). Trả về danh sách gồm id, tên, URL, lĩnh vực, thumbnail.',
                parameters: {
                    type: 'OBJECT' as const,
                    properties: {
                        field: {
                            type: 'STRING',
                            description: 'Lọc theo lĩnh vực/field: tourism, factory, farm, heritage, museum, city, facility, school, oil-and-gas, real-estate, office, zone, hospital, port. Bỏ trống để lấy tất cả.',
                        },
                        search: {
                            type: 'STRING',
                            description: 'Từ khóa tìm kiếm dự án (tên, địa điểm). Bỏ trống để lấy tất cả.',
                        },
                        page: {
                            type: 'NUMBER',
                            description: 'Trang (mặc định 1). Mỗi trang 20 dự án.',
                        },
                    },
                },
            },
            handler: async (args: { field?: string; search?: string; page?: number }) => {
                try {
                    const params: Record<string, any> = {
                        page: args.page || 1,
                        limit: 20,
                        active: 1,
                        map: '34',
                    };
                    if (args.field) params.field = args.field;
                    if (args.search) params.search = args.search;

                    const response = await projectService.getProjectsByLocation(params);
                    const projects = response?.data || response || [];

                    if (!Array.isArray(projects) || projects.length === 0) {
                        return {
                            success: true,
                            projects: [],
                            message: 'Không tìm thấy dự án nào phù hợp.',
                            instruction: 'Thông báo cho người dùng rằng không có dự án phù hợp. Gợi ý thử lĩnh vực hoặc từ khóa khác.',
                        };
                    }

                    const projectList = projects.map((p: any) => ({
                        id: p.id,
                        name: p.title || p.display_vn || p.display_en || 'Không tên',
                        url: p.url || p.URL || '',
                        field: p.field || '',
                        thumbnail: p.thumbnail || '',
                    }));

                    // Ưu tiên dự án không chứa 'sanpham' trong URL lên trước
                    projectList.sort((a: any, b: any) => {
                        const aHasSanPham = (a.url || '').toLowerCase().includes('sanpham');
                        const bHasSanPham = (b.url || '').toLowerCase().includes('sanpham');
                        if (aHasSanPham && !bHasSanPham) return 1;
                        if (!aHasSanPham && bHasSanPham) return -1;
                        return 0;
                    });

                    return {
                        success: true,
                        total: projectList.length,
                        projects: projectList,
                        instruction: 'Đọc danh sách dự án và trình bày cho người dùng. Khi mở dự án có tên trùng nhau, TUYỆT ĐỐI ƯU TIÊN chọn URL KHÔNG chứa "sanpham". Chỉ chọn URL chứa "sanpham" nếu người dùng nói rõ là muốn mở trang "sản phẩm". Dùng URL chính xác từ danh sách để gọi open_project_in_viewer.',
                    };
                } catch (err: any) {
                    return {
                        success: false,
                        error: err.message || 'Lỗi khi lấy danh sách dự án',
                        instruction: 'Thông báo lỗi kỹ thuật cho người dùng và gợi ý thử lại.',
                    };
                }
            },
        },

        /* ═══════════════════════════════════════════════════════════
         * SUB-PROJECT PROXY TOOLS
         * These forward commands to the iframe via postMessage and
         * return the result. They only work when a project is open.
         * ═══════════════════════════════════════════════════════════ */

        // ─── Scene Navigation ───

        ...(() => {
            const tools: Record<string, GeminiTool> = {};

            tools.list_scene_names = proxyTool(
                'list_scene_names',
                'Liệt kê tất cả các scene (điểm tham quan, địa điểm, view 360°) trong dự án đang mở. Chỉ hoạt động khi đã mở một dự án trong trình xem.',
                {
                    type: 'OBJECT' as const,
                    properties: {
                        language: {
                            type: 'STRING',
                            description: "Mã ngôn ngữ: 'vn' cho Tiếng Việt, 'en' cho English",
                        },
                    },
                },
                ctx,
            );

            tools.navigate_to_scene = proxyTool(
                'navigate_to_scene',
                'Di chuyển đến một scene/điểm tham quan cụ thể trong dự án đang mở. CHỈ truyền tên địa điểm, KHÔNG lấy cả câu.',
                {
                    type: 'OBJECT' as const,
                    properties: {
                        scene_query: {
                            type: 'STRING',
                            description: "CHỈ tên địa điểm. Ví dụ: nếu user nói 'dẫn tôi đến nhà máy' thì chỉ truyền 'nhà máy'.",
                        },
                    },
                    required: ['scene_query'],
                },
                ctx,
            );

            tools.get_current_scene_info = proxyTool(
                'get_current_scene_info',
                'Lấy thông tin cảnh 360° đang xem và danh sách các ĐIỂM THAM QUAN / HOTSPOT ĐIỀU HƯỚNG (mũi tên chuyển cảnh / địa điểm có thể đi tiếp từ view này). BẮT BUỘC ƯU TIÊN GỌI TOOL NÀY TRƯỚC TIÊN khi người dùng hỏi: "Có những địa điểm nào tham quan?", "Ở đây có những gì?", "Có thể đi đâu?", "View này có những điều hướng nào?", "Từ đây đi đâu được?".',
                { type: 'OBJECT' as const, properties: {} },
                ctx,
            );

            // ─── 3D Models ───

            tools.get_all_model_names = proxyTool(
                'get_all_model_names',
                'Liệt kê tất cả mô hình 3D có trong dự án đang mở.',
                { type: 'OBJECT' as const, properties: {} },
                ctx,
            );

            tools.open_model = proxyTool(
                'open_model',
                'Mở và hiển thị một mô hình 3D cụ thể trong dự án. Dùng khi người dùng muốn xem chi tiết thiết bị, máy móc.',
                {
                    type: 'OBJECT' as const,
                    properties: {
                        model_query: {
                            type: 'STRING',
                            description: 'Tên hoặc từ khóa của mô hình 3D cần mở',
                        },
                    },
                    required: ['model_query'],
                },
                ctx,
            );

            // ─── Photos ───

            tools.get_all_photo_names = proxyTool(
                'get_all_photo_names',
                'Liệt kê tất cả ảnh/hình ảnh có trong dự án đang mở.',
                { type: 'OBJECT' as const, properties: {} },
                ctx,
            );

            tools.open_photo = proxyTool(
                'open_photo',
                'Mở và hiển thị một ảnh cụ thể trong dự án đang mở.',
                {
                    type: 'OBJECT' as const,
                    properties: {
                        photo_query: {
                            type: 'STRING',
                            description: 'Tên hoặc từ khóa của ảnh cần mở',
                        },
                    },
                    required: ['photo_query'],
                },
                ctx,
            );

            // ─── Videos ───

            tools.get_all_video_names = proxyTool(
                'get_all_video_names',
                'Liệt kê tất cả video có trong dự án đang mở.',
                { type: 'OBJECT' as const, properties: {} },
                ctx,
            );

            tools.open_video = proxyTool(
                'open_video',
                'Mở và phát một video cụ thể trong dự án đang mở.',
                {
                    type: 'OBJECT' as const,
                    properties: {
                        video_query: {
                            type: 'STRING',
                            description: 'Tên hoặc từ khóa của video cần mở',
                        },
                    },
                    required: ['video_query'],
                },
                ctx,
            );


            // ─── Virtual Tour / Autotour ───

            tools.create_virtual_tour = proxyTool(
                'create_virtual_tour',
                "Create and start a NEW virtual tour with live AI narration. Use when user asks to 'tham quan', 'tạo tour', or wants to visit locations with YOUR narration.",
                {
                    type: 'OBJECT' as const,
                    properties: {
                        tour_name: {
                            type: 'STRING',
                            description: "Name for the tour (e.g., 'Tham quan nhà máy')",
                        },
                        scene_queries: {
                            type: 'STRING',
                            description: "Comma-separated location names to include",
                        },
                        scenes_per_location: {
                            type: 'ARRAY',
                            items: { type: 'NUMBER' },
                            description: "Array of random numbers between 5-20 for each location",
                        },
                        narration: {
                            type: 'STRING',
                            description: "YOUR complete tour guide narration script. Write at least 100 words per location.",
                        },
                    },
                    required: ['tour_name', 'scene_queries', 'scenes_per_location', 'narration'],
                },
                ctx,
            );

            tools.stop_virtual_tour = proxyTool(
                'stop_virtual_tour',
                'Dừng tour tham quan ảo đang chạy trong dự án.',
                { type: 'OBJECT' as const, properties: {} },
                ctx,
            );

            tools.list_existing_autotours = proxyTool(
                'list_existing_autotours',
                'Liệt kê các tour tham quan tự động có sẵn trong dự án đang mở.',
                { type: 'OBJECT' as const, properties: {} },
                ctx,
            );

            tools.start_existing_autotour = proxyTool(
                'start_existing_autotour',
                'Bắt đầu một tour tham quan tự động đã có sẵn trong dự án.',
                {
                    type: 'OBJECT' as const,
                    properties: {
                        autotour_query: {
                            type: 'STRING',
                            description: 'Tên hoặc từ khóa của autotour cần bắt đầu',
                        },
                    },
                    required: ['autotour_query'],
                },
                ctx,
            );

            // ─── IoT & Monitoring ───

            tools.show_iot_monitoring_dashboard = proxyTool(
                'show_iot_monitoring_dashboard',
                'Mở bảng điều khiển IoT monitoring (giám sát thiết bị, cảm biến thời gian thực).',
                { type: 'OBJECT' as const, properties: {} },
                ctx,
            );

            tools.close_iot_monitoring_dashboard = proxyTool(
                'close_iot_monitoring_dashboard',
                'Đóng bảng điều khiển IoT monitoring.',
                { type: 'OBJECT' as const, properties: {} },
                ctx,
            );

            tools.show_fire_emergency_system = proxyTool(
                'show_fire_emergency_system',
                'Mở hệ thống cảnh báo cháy / PCCC trong dự án đang mở.',
                { type: 'OBJECT' as const, properties: {} },
                ctx,
            );

            tools.close_fire_emergency_system = proxyTool(
                'close_fire_emergency_system',
                'Đóng hệ thống cảnh báo cháy / PCCC.',
                { type: 'OBJECT' as const, properties: {} },
                ctx,
            );

            // ─── UI Panels ───

            tools.open_introduction = proxyTool(
                'open_introduction',
                'Mở panel giới thiệu dự án đang xem.',
                { type: 'OBJECT' as const, properties: {} },
                ctx,
            );

            tools.close_introduction = proxyTool(
                'close_introduction',
                'Đóng panel giới thiệu.',
                { type: 'OBJECT' as const, properties: {} },
                ctx,
            );

            tools.open_minimap = proxyTool(
                'open_minimap',
                'Mở bản đồ minimap của dự án đang xem.',
                { type: 'OBJECT' as const, properties: {} },
                ctx,
            );

            tools.close_minimap = proxyTool(
                'close_minimap',
                'Đóng bản đồ minimap của dự án.',
                { type: 'OBJECT' as const, properties: {} },
                ctx,
            );

            tools.open_cesium_map = proxyTool(
                'open_cesium_map',
                'Mở bản đồ 3D Cesium của dự án đang xem (đặc biệt trong dự án QTSC).',
                { type: 'OBJECT' as const, properties: {} },
                ctx,
            );

            tools.close_cesium_map = proxyTool(
                'close_cesium_map',
                'Đóng bản đồ 3D Cesium của dự án.',
                { type: 'OBJECT' as const, properties: {} },
                ctx,
            );

            tools.open_minimap_qtsc = proxyTool(
                'open_minimap_qtsc',
                'Mở minimap và lọc khu vực báo lỗi (dành riêng cho dự án QTSC).',
                { type: 'OBJECT' as const, properties: {} },
                ctx,
            );

            tools.close_minimap_qtsc = proxyTool(
                'close_minimap_qtsc',
                'Đóng minimap QTSC.',
                { type: 'OBJECT' as const, properties: {} },
                ctx,
            );


            tools.open_material_panel = proxyTool(
                'open_material_panel',
                'Mở panel tài liệu/thư viện nội dung (ảnh, video, mô hình 3D, sách).',
                { type: 'OBJECT' as const, properties: {} },
                ctx,
            );

            tools.close_material_panel = proxyTool(
                'close_material_panel',
                'Đóng panel tài liệu/thư viện nội dung.',
                { type: 'OBJECT' as const, properties: {} },
                ctx,
            );

            tools.switch_material_tab = proxyTool(
                'switch_material_tab',
                'Chuyển tab trong panel thư viện nội dung (ảnh, video, mô hình 3D, sách).',
                {
                    type: 'OBJECT' as const,
                    properties: {
                        tab_name: {
                            type: 'STRING',
                            description: "Tên tab: 'photo', 'video', '3d_model', 'book'",
                        },
                    },
                    required: ['tab_name'],
                },
                ctx,
            );

            // ─── 3D Library & Material Asset Detail ───

            tools.open_3d_library = proxyTool(
                'open_3d_library',
                'Mở thư viện 3D trong dự án đang mở.',
                { type: 'OBJECT' as const, properties: {} },
                ctx,
            );

            tools.close_3d_library = proxyTool(
                'close_3d_library',
                'Đóng thư viện 3D đang mở.',
                { type: 'OBJECT' as const, properties: {} },
                ctx,
            );

            tools.close_material_asset_detail = proxyTool(
                'close_material_asset_detail',
                'Đóng chi tiết tài liệu/tư liệu đang hiển thị (ảnh, video, mô hình 3D, sách đã mở).',
                { type: 'OBJECT' as const, properties: {} },
                ctx,
            );

            // ─── Other UI Panels ───

            tools.close_contact = proxyTool(
                'close_contact',
                'Đóng form liên hệ.',
                { type: 'OBJECT' as const, properties: {} },
                ctx,
            );

            tools.close_settings = proxyTool(
                'close_settings',
                'Đóng bảng cài đặt.',
                { type: 'OBJECT' as const, properties: {} },
                ctx,
            );

            tools.close_autotour_panel = proxyTool(
                'close_autotour_panel',
                'Đóng panel danh sách auto tour.',
                { type: 'OBJECT' as const, properties: {} },
                ctx,
            );

            tools.close_blog = proxyTool(
                'close_blog',
                'Đóng trang blog/tin tức.',
                { type: 'OBJECT' as const, properties: {} },
                ctx,
            );

            tools.close_sidebar_menu = proxyTool(
                'close_sidebar_menu',
                'Đóng menu sidebar.',
                { type: 'OBJECT' as const, properties: {} },
                ctx,
            );

            // ─── Language ───

            tools.switch_language = {
                declaration: {
                    name: 'switch_language',
                    description: 'Chuyển ngôn ngữ hiển thị của nền tảng (nếu không có dự án) hoặc của dự án đang mở.',
                    parameters: {
                        type: 'OBJECT',
                        properties: {
                            language_id: {
                                type: 'STRING',
                                description: "Mã ngôn ngữ: 'vn', 'en', 'fr', 'de', v.v.",
                            },
                        },
                        required: ['language_id'],
                    },
                },
                handler: async (args: any) => {
                    const project = ctx.getActiveProject();
                    if (project) {
                        return ctx.sendIframeCommand('switch_language', args);
                    }
                    if (typeof window !== 'undefined' && (window as any).changeLocale) {
                        (window as any).changeLocale(args.language_id);
                        return { result: `Đã chuyển ngôn ngữ nền tảng sang ${args.language_id}.` };
                    }
                    return { error: 'Không thể chuyển ngôn ngữ lúc này.' };
                }
            };

            // ─── Audio ───

            tools.toggle_audio = proxyTool(
                'toggle_audio',
                'Bật/tắt âm thanh nền trong dự án đang mở.',
                {
                    type: 'OBJECT' as const,
                    properties: {
                        action: {
                            type: 'STRING',
                            description: "'play' để bật, 'pause' để tắt, 'toggle' để đổi trạng thái",
                        },
                    },
                },
                ctx,
            );

            // ─── Close Content Viewer (model / photo / video / book) ───

            tools.close_tab = proxyTool(
                'close_tab',
                'Đóng tab nội dung đang mở (mô hình 3D / video / ảnh / sách). Dùng khi người dùng yêu cầu đóng model, đóng ảnh, đóng video, đóng tài liệu. KHÔNG dùng tool này để đóng popup gợi ý địa điểm (dùng close_recommendations_popup thay vào đó).',
                {
                    type: 'OBJECT' as const,
                    properties: {},
                },
                ctx,
            );

            return tools;
        })(),

        // ─── Global Parent UI Tools (run on parent window, not iframe) ───

        close_recommendations_popup: {
            declaration: {
                name: 'close_recommendations_popup',
                description: 'Đóng popup gợi ý địa điểm.',
                parameters: { type: 'OBJECT' as const, properties: {} },
            },
            handler: async () => {
                if (typeof window !== 'undefined') {
                    window.dispatchEvent(new CustomEvent('close_recommendations'));
                }
                return { success: true, message: 'Recommendations popup closed' };
            },
        },

        show_recommendations: {
            declaration: {
                name: 'show_recommendations',
                description:
                    'Hiển thị gợi ý địa điểm ăn uống, vui chơi, lưu trú xung quanh khu vực hiện tại. Gọi khi người dùng hỏi về địa điểm gần đây.',
                parameters: {
                    type: 'OBJECT' as const,
                    properties: {
                        location: {
                            type: 'STRING',
                            description: "Tên thành phố/khu vực (e.g., 'Hanoi', 'Ho Chi Minh City')",
                        },
                        eat: {
                            type: 'ARRAY',
                            description: 'Danh sách ít nhất 3 địa điểm ăn uống.',
                            items: {
                                type: 'OBJECT',
                                properties: {
                                    name: { type: 'STRING' },
                                    description: { type: 'STRING' },
                                    rating: { type: 'NUMBER' },
                                    address: { type: 'STRING' },
                                },
                            },
                        },
                        fun: {
                            type: 'ARRAY',
                            description: 'Danh sách ít nhất 3 địa điểm vui chơi.',
                            items: {
                                type: 'OBJECT',
                                properties: {
                                    name: { type: 'STRING' },
                                    description: { type: 'STRING' },
                                    rating: { type: 'NUMBER' },
                                    address: { type: 'STRING' },
                                },
                            },
                        },
                        stay: {
                            type: 'ARRAY',
                            description: 'Danh sách ít nhất 3 nơi lưu trú.',
                            items: {
                                type: 'OBJECT',
                                properties: {
                                    name: { type: 'STRING' },
                                    description: { type: 'STRING' },
                                    rating: { type: 'NUMBER' },
                                    address: { type: 'STRING' },
                                },
                            },
                        },
                    },
                    required: ['location', 'eat', 'fun', 'stay'],
                },
            },
            handler: async (args: any) => {
                if (typeof window !== 'undefined') {
                    window.dispatchEvent(new CustomEvent('show_recommendations', { detail: args }));
                }
                return {
                    success: true,
                    message: `Recommendations for ${args.location} displayed.`,
                    instruction: `Giới thiệu cho người dùng các gợi ý địa điểm tại ${args.location}.`,
                };
            },
        },

        /* ═══════════════════════════════════════════════════════════
         * SECTOR TOOLS (run on parent window)
         * Mở/đóng danh sách lĩnh vực, danh sách dự án theo lĩnh vực
         * ═══════════════════════════════════════════════════════════ */

        get_sectors: {
            declaration: {
                name: 'get_sectors',
                description: 'Lấy danh sách tất cả lĩnh vực/ngành nghề (Tourism, Heritage, Smart City, v.v.) và tự động mở panel lĩnh vực. Dùng khi người dùng hỏi về các lĩnh vực, ngành nghề, hoặc yêu cầu xem danh sách lĩnh vực.',
                parameters: { type: 'OBJECT' as const, properties: {} },
            },
            handler: async () => {
                const result = await functionCallingServices.getSectors();
                try {
                    await functionCallingServices.openSectorList();
                } catch (e) {
                    console.warn('Failed to call openSectorList API:', e);
                }
                if (typeof window !== 'undefined') {
                    window.dispatchEvent(new CustomEvent('open_sector_list'));
                }
                return result;
            },
        },

        open_sector_list: {
            declaration: {
                name: 'open_sector_list',
                description: 'Mở danh sách các lĩnh vực trên giao diện. Dùng khi người dùng yêu cầu mở hoặc xem danh sách các lĩnh vực áp dụng công nghệ.',
                parameters: { type: 'OBJECT' as const, properties: {} },
            },
            handler: async () => {
                const result = await functionCallingServices.openSectorList();
                if (typeof window !== 'undefined') {
                    window.dispatchEvent(new CustomEvent('open_sector_list'));
                }
                return result;
            },
        },

        close_sector_list: {
            declaration: {
                name: 'close_sector_list',
                description: 'Đóng danh sách lĩnh vực trên giao diện.',
                parameters: { type: 'OBJECT' as const, properties: {} },
            },
            handler: async () => {
                const result = await functionCallingServices.closeSectorList();
                if (typeof window !== 'undefined') {
                    window.dispatchEvent(new CustomEvent('close_sector_list'));
                }
                return result;
            },
        },

        open_sector_project_list: {
            declaration: {
                name: 'open_sector_project_list',
                description: 'Mở danh sách dự án trong một lĩnh vực cụ thể trên giao diện. Kết hợp với list_platform_projects để người dùng xem được danh sách dự án.',
                parameters: {
                    type: 'OBJECT' as const,
                    properties: {
                        sector: {
                            type: 'STRING',
                            description: "Mã lĩnh vực: 'city', 'exhibition', 'facility', 'factory', 'farm', 'heritage', 'museum', 'office', 'oil-and-gas', 'real-estate', 'school', 'showroom', 'tourism', 'zone'.",
                        },
                    },
                    required: ['sector'],
                },
            },
            handler: async (args: { sector: string }) => {
                const result = await functionCallingServices.openSectorProjectList();
                if (typeof window !== 'undefined') {
                    window.dispatchEvent(new CustomEvent('open_sector_project_list', { detail: { sector: args.sector } }));
                }
                return result;
            },
        },

        close_sector_project_list: {
            declaration: {
                name: 'close_sector_project_list',
                description: 'Đóng danh sách dự án của lĩnh vực đang mở.',
                parameters: { type: 'OBJECT' as const, properties: {} },
            },
            handler: async () => {
                const result = await functionCallingServices.closeSectorProjectList();
                if (typeof window !== 'undefined') {
                    window.dispatchEvent(new CustomEvent('close_sector_project_list'));
                }
                return result;
            },
        },

        /* ═══════════════════════════════════════════════════════════
         * MAP TOOLS (run on parent window)
         * Mở/đóng bản đồ 3D, lọc dự án, chỉ đường
         * ═══════════════════════════════════════════════════════════ */

        open_map: {
            declaration: {
                name: 'open_map',
                description: 'Mở bản đồ số hóa 3D (Map 3D) trên trang chủ. Dùng khi người dùng yêu cầu mở bản đồ, xem bản đồ số hóa, xem bản đồ 3D.',
                parameters: { type: 'OBJECT' as const, properties: {} },
            },
            handler: async () => {
                if (typeof window !== 'undefined') {
                    window.dispatchEvent(new CustomEvent('open_map_directions'));
                }
                return { success: true, message: 'Đã mở bản đồ số hóa.' };
            },
        },

        close_map: {
            declaration: {
                name: 'close_map',
                description: 'Đóng bản đồ số hóa 3D và quay về trang chủ. Chỉ dùng khi người dùng muốn ĐÓNG/THOÁT/TẮT bản đồ.',
                parameters: { type: 'OBJECT' as const, properties: {} },
            },
            handler: async () => {
                if (typeof window !== 'undefined') {
                    window.dispatchEvent(new CustomEvent('close_map'));
                }
                return { success: true, message: 'Đã đóng bản đồ số hóa.' };
            },
        },

        open_map_directions: {
            declaration: {
                name: 'open_map_directions',
                description: 'Mở chế độ chỉ đường trên bản đồ. Dùng khi người dùng hỏi hướng dẫn đường đi, chỉ đường tới dự án.',
                parameters: {
                    type: 'OBJECT' as const,
                    properties: {
                        project_url: {
                            type: 'STRING',
                            description: 'URL dự án để chỉ đường tới. Bắt buộc khi muốn chỉ đường tới dự án cụ thể.',
                        },
                    },
                },
            },
            handler: async (args: { project_url?: string }) => {
                if (typeof window !== 'undefined') {
                    window.dispatchEvent(new CustomEvent('open_map_directions', { detail: { project_url: args?.project_url } }));
                }
                return { success: true, message: 'Đã mở bản đồ số hóa hiển thị thông tin dự án.' };
            },
        },

        close_map_directions: {
            declaration: {
                name: 'close_map_directions',
                description: 'Tắt chế độ chỉ đường trên bản đồ để quay lại xem bình thường.',
                parameters: { type: 'OBJECT' as const, properties: {} },
            },
            handler: async () => {
                if (typeof window !== 'undefined') {
                    window.dispatchEvent(new CustomEvent('close_map_directions'));
                }
                return { success: true, message: 'Đã tắt chế độ dẫn đường.' };
            },
        },

        focus_map_project: {
            declaration: {
                name: 'focus_map_project',
                description: 'Zoom bản đồ tới dự án cụ thể và mở popup thông tin. Dùng khi người dùng muốn xem vị trí dự án trên bản đồ.',
                parameters: {
                    type: 'OBJECT' as const,
                    properties: {
                        project_url: {
                            type: 'STRING',
                            description: 'URL dự án cần zoom tới.',
                        },
                    },
                    required: ['project_url'],
                },
            },
            handler: async (args: { project_url: string }) => {
                if (typeof window !== 'undefined') {
                    window.dispatchEvent(new CustomEvent('focus_map_project', { detail: { project_url: args.project_url } }));
                }
                return { success: true, message: 'Đã đưa bản đồ tới dự án.' };
            },
        },

        filter_map_projects: {
            declaration: {
                name: 'filter_map_projects',
                description: 'Lọc dự án trên bản đồ theo miền, lĩnh vực, tỉnh thành. Dùng khi người dùng yêu cầu lọc/tìm kiếm dự án trên bản đồ.',
                parameters: {
                    type: 'OBJECT' as const,
                    properties: {
                        region: {
                            type: 'STRING',
                            description: "Miền: 'Miền Bắc', 'Miền Trung', 'Miền Nam'. Bỏ trống nếu không lọc.",
                        },
                        fields: {
                            type: 'ARRAY',
                            items: { type: 'STRING' },
                            description: "Mảng lĩnh vực: 'tourism', 'heritage', 'museum', 'city', 'oil-and-gas', 'factory', 'zone', 'facility', 'showroom', 'real-estate', 'exhibition', 'office', 'school', 'farm'.",
                        },
                        provinces: {
                            type: 'ARRAY',
                            items: { type: 'STRING' },
                            description: "Mảng tên tỉnh thành (viết đúng tiếng Việt có dấu).",
                        },
                        country: {
                            type: 'STRING',
                            description: "Tên quốc gia (ví dụ: 'Việt Nam').",
                        },
                    },
                },
            },
            handler: async (args: any) => {
                if (typeof window !== 'undefined') {
                    const detail = { region: args?.region, fields: args?.fields, provinces: args?.provinces, country: args?.country };
                    window.dispatchEvent(new CustomEvent('filter_map_projects', { detail }));
                }
                return { success: true, message: 'Đã áp dụng bộ lọc trên bản đồ.' };
            },
        },

        switch_province_mode: {
            declaration: {
                name: 'switch_province_mode',
                description: 'Chuyển chế độ bản đồ giữa 34 và 63 tỉnh thành.',
                parameters: {
                    type: 'OBJECT' as const,
                    properties: {
                        mode: {
                            type: 'STRING',
                            description: "'34' hoặc '63'",
                        },
                    },
                    required: ['mode'],
                },
            },
            handler: async (args: { mode: string }) => {
                if (typeof window !== 'undefined') {
                    window.dispatchEvent(new CustomEvent('switch_province_mode', { detail: { mode: args.mode } }));
                }
                return { success: true, message: `Đã chuyển bản đồ sang ${args.mode} tỉnh thành.` };
            },
        },

        reset_map_filter: {
            declaration: {
                name: 'reset_map_filter',
                description: 'Reset bộ lọc bản đồ, quay lại toàn cảnh hiển thị tất cả dự án.',
                parameters: { type: 'OBJECT' as const, properties: {} },
            },
            handler: async () => {
                if (typeof window !== 'undefined') {
                    window.dispatchEvent(new CustomEvent('reset_map_filter'));
                }
                return { success: true, message: 'Đã reset bộ lọc bản đồ.' };
            },
        },
    };
}

/* ─── System instruction for Platform mode ─── */
export function getPlatformSystemInstruction(avatarName: string, sectorName?: string): string {
    const greeting = sectorName
        ? `Xin chào, tôi là ${avatarName}, chuyên gia số AI để đồng hành và hướng dẫn Anh Chị khám phá các giải pháp Bản sao số cho lĩnh vực ${sectorName}.`
        : `Bạn là ${avatarName} — Hướng dẫn viên ảo 3D thông minh của StarGlobal 3D trên nền tảng platform.starglobal3d.com.`;

    const contextBlock = sectorName
        ? `🌐 BẠN ĐANG Ở TRANG LĨNH VỰC: ${sectorName}
Nếu người dùng hỏi "Đây là lĩnh vực gì", BẮT BUỘC trả lời ngay là: "Anh chị đang ở lĩnh vực ${sectorName} tương tác thông minh 3D/360".`
        : `🌐 BẠN ĐANG Ở CHẾ ĐỘ PLATFORM (TRANG TỔNG):
Trang Platform hiển thị 12+ lĩnh vực công nghệ (du lịch, nhà máy, bất động sản, trường học, bệnh viện, khu công nghiệp, v.v.) và hàng trăm dự án 3D/360 bên trong mỗi lĩnh vực.`;

    return `${greeting}

${contextBlock}

📋 KHẢ NĂNG CỦA BẠN:
1. LIỆT KÊ DỰ ÁN: Gọi list_platform_projects() để lấy danh sách dự án thực từ database. Có thể lọc theo lĩnh vực hoặc tìm kiếm.
2. MỞ DỰ ÁN: Sau khi có danh sách, gọi open_project_in_viewer() với URL chính xác từ danh sách.
3. ĐÓNG DỰ ÁN: Gọi close_project_viewer() để quay về trang Platform.
4. KIỂM TRA: get_current_viewer_project() để xem dự án nào đang mở.
5. NGÔN NGỮ: Gọi switch_language(language_id) để chuyển ngôn ngữ nền tảng (nếu ở ngoài trang chủ) hoặc dự án (nếu đang mở dự án).

📂 LĨNH VỰC (SECTORS):
- get_sectors: Lấy danh sách tất cả lĩnh vực và tự động mở panel lĩnh vực
- open_sector_list / close_sector_list: Mở/đóng danh sách lĩnh vực
- open_sector_project_list(sector): Mở danh sách dự án trong lĩnh vực cụ thể (truyền mã: 'tourism', 'factory', 'heritage', 'museum', 'city', 'farm', v.v.)
- close_sector_project_list: Đóng danh sách dự án lĩnh vực

🗺️ BẢN ĐỒ SỐ HÓA 3D:
- open_map: Mở bản đồ số hóa 3D của NỀN TẢNG (không dùng lệnh này để mở bản đồ Cesium trong dự án)
- close_map: Đóng bản đồ NỀN TẢNG
- open_map_directions / close_map_directions: Mở/tắt chế độ chỉ đường
- focus_map_project(project_url): Zoom tới dự án cụ thể trên bản đồ
- filter_map_projects(region, fields, provinces, country): Lọc dự án trên bản đồ
- switch_province_mode(mode): Chuyển chế độ 34/63 tỉnh thành
- reset_map_filter: Reset bộ lọc, quay lại toàn cảnh

🏗️ TƯƠNG TÁC TRONG DỰ ÁN ĐÃ MỞ:
Sau khi mở một dự án bằng open_project_in_viewer, bạn CÓ THỂ tương tác trực tiếp:
- navigate_to_scene: Di chuyển đến các điểm tham quan, scene, view 360°
- list_scene_names: Liệt kê tất cả các scene có trong dự án
- get_current_scene_info: Xem thông tin scene đang hiển thị
- open_model / get_all_model_names: Xem và mở mô hình 3D
- open_photo / open_video: Xem ảnh và video
- close_tab: ĐÓNG nội dung đang mở (model 3D, ảnh, video, sách).
- open_cesium_map / close_cesium_map: Mở/đóng bản đồ 3D Cesium bên TRONG dự án đang xem (VD: QTSC). Dùng lệnh này khi người dùng nói 'mở bản đồ 3d cesium'.
- open_minimap_qtsc / close_minimap_qtsc: Mở/đóng minimap đặc biệt lọc lỗi của QTSC.
- close_material_asset_detail: Đóng chi tiết tài liệu/tư liệu
- close_3d_library: Đóng thư viện 3D
- show_iot_monitoring_dashboard / close_iot_monitoring_dashboard: Mở/đóng bảng giám sát IoT
- show_fire_emergency_system / close_fire_emergency_system: Mở/đóng hệ thống cảnh báo PCCC
- create_virtual_tour / start_existing_autotour / stop_virtual_tour: Tạo, bắt đầu, dừng tour
- close_autotour_panel: Đóng panel danh sách tour
- open_introduction / close_introduction: Mở/đóng giới thiệu
- open_minimap / close_minimap: Mở/đóng bản đồ minimap
- open_material_panel / close_material_panel / switch_material_tab: Thư viện nội dung
- switch_language: Chuyển ngôn ngữ dự án (hoặc nền tảng)
- toggle_audio: Bật/tắt âm thanh nền
- close_sidebar_menu / close_blog / close_contact / close_settings: Đóng các panel UI khác

🔴 QUY TẮC BẮT BUỘC:
1. TUYỆT ĐỐI KHÔNG tự bịa tên hoặc URL dự án. Phải gọi list_platform_projects() trước để lấy danh sách thực.
2. Khi người dùng yêu cầu mở dự án → gọi list_platform_projects() (có thể kèm search/field) → tìm dự án phù hợp → gọi open_project_in_viewer() với URL chính xác từ kết quả. Nếu có nhiều dự án trùng tên, TUYỆT ĐỐI ƯU TIÊN chọn dự án KHÔNG chứa "sanpham" trong URL (trừ khi người dùng nói rõ muốn xem trang sản phẩm).
3. Nếu người dùng nói tên dự án không rõ → gọi list_platform_projects(search: "từ khóa") để tìm.
4. Nếu không tìm thấy dự án → thông báo lịch sự và gợi ý lĩnh vực khác.
5. Nếu người dùng yêu cầu tương tác nhưng chưa mở dự án → nhắc mở trước.
6. Nếu tool trả lỗi timeout → dự án chưa load xong, báo lịch sự.
7. Có thể kết hợp: liệt kê → mở dự án → dẫn đi scene → mở model 3D → tạo tour.
8. Khi mở dự án mới, liệt kê scene và gợi ý điểm nổi bật.
9. Nếu đang ở trang lĩnh vực (ví dụ Bất Động Sản), câu chào BẮT BUỘC phải giới thiệu rõ về lĩnh vực đó thay vì chỉ nói chung chung.

🎯 QUY TẮC ĐẶC BIỆT KHI HỎI VỀ ĐIỂM THAM QUAN / ĐIỀU HƯỚNG:
- Khi người dùng đang ở trong một dự án và hỏi về ĐIỂM THAM QUAN / ĐIỀU HƯỚNG (ví dụ: "Có những địa điểm nào tham quan?", "Có thể đi đâu?", "Ở đây có những gì?", "Khu này có những điểm nào?", "View này có những điều hướng nào?"):
  1. BẮT BUỘC ƯU TIÊN GỌI \`get_current_scene_info\` TRƯỚC TIÊN để lấy danh sách các HOTSPOT ĐIỀU HƯỚNG (các điểm đến trực tiếp từ view 360° đang xem, giống như cơ chế của dự án Việt Nam).
  2. Khi tool trả về thông tin kèm danh sách hotspot (Available locations to visit from here), bạn PHẢI ĐỌC TÊN TỪNG ĐIỂM HOTSPOT đó ra cho người dùng.
  3. CHỈ KHI view hiện tại không có hotspot nào, hoặc khi người dùng yêu cầu rõ ràng "xem toàn bộ các cảnh / phân khu trong dự án", bạn mới gọi \`list_scene_names\`.

💡 PHONG CÁCH:
- Nói chuyện tự nhiên, thân thiện, chuyên nghiệp
- Chủ động gợi ý dự án liên quan sau khi xem xong
- Khi vào dự án mới hoặc khi được hỏi về điểm tham quan, hãy ưu tiên gọi get_current_scene_info để xem các hotspot/điểm đến từ view hiện tại.
- TUYỆT ĐỐI TRÁNH lặp lại từ "Vâng, ..." ở đầu mỗi câu trả lời. Hãy trả lời trực tiếp, đa dạng cách mở đầu và hạn chế tối đa dùng từ "Vâng".`;
}
